import type { Assessment } from './assessment';

export const exampleResume = `ALEX MORGAN
Product Operations Manager

SUMMARY
Product operations leader with 7 years' experience helping SaaS teams turn customer insight into clearer priorities and reliable launches.

EXPERIENCE
Northstar, Product Operations Manager | 2021–Present
• Built a customer feedback system used by product, support and sales; reduced duplicate requests by 35%.
• Ran quarterly planning and launch readiness across a 4-squad product organisation.
• Created dashboards in SQL and Looker to track adoption, churn signals and onboarding friction.
• Managed two operations specialists and coached product managers on experiment briefs.

Brightside, Customer Success Lead | 2018–2021
• Managed 45 B2B accounts and improved renewal rate from 83% to 91% over two years.
• Partnered with engineering on integrations and triaged technical customer issues.

EDUCATION
Bachelor of Commerce, University of Western Australia`;

export const exampleJobAd = `Senior Product Operations Lead
Perth or hybrid | Full-time

We are looking for a Senior Product Operations Lead to make our product organisation more focused, measurable and connected to customers.

You will:
• Own the product planning and operating rhythm across multiple squads.
• Build lightweight systems for customer insight, prioritisation and launch readiness.
• Partner with Product, Engineering, Design, Sales and Customer Success.
• Coach product managers and help leaders use evidence in decision-making.
• Define metrics and create clear reporting for product adoption and outcomes.

You bring:
• 6+ years in product operations, product management, strategy or a related role.
• Experience working across several product squads in a SaaS or technology business.
• Strong analytical communication skills; SQL or BI experience is useful.
• A practical, low-ego approach and comfort with ambiguity.

Salary and reporting line will be discussed during the process.`;

export const mockAssessment: Assessment = {
  score: 84,
  rating: 'Strong match',
  verdict: 'Apply',
  verdictSummary: 'Your background lines up unusually well with the operating problems this role is trying to solve. You have direct evidence of planning rhythms, customer insight systems, launch readiness and cross-functional work in SaaS; the main question is whether the scope of “senior” matches the size of the organisation you have led.',
  scoreBreakdown: {
    essentialSkillsQualifications: 88,
    relevantExperienceAchievements: 86,
    seniorityResponsibilities: 78,
    industryTechnicalContext: 82,
    practicalAlignment: 74,
  },
  strengths: [
    'Seven years in adjacent product operations and customer-facing roles meets the experience bar, with a recent move into direct product-ops ownership.',
    'You have a quantified example of building a customer feedback system and reducing duplicate requests by 35%, which maps closely to the insight-system brief.',
    'Quarterly planning and launch readiness across four squads is strong evidence for the role’s cross-squad operating rhythm.',
    'SQL and Looker dashboards, plus adoption and churn reporting, give you credible analytical context beyond simple project coordination.',
  ],
  gaps: [
    { label: 'Scope of senior leadership', detail: 'The resume shows management of two specialists and influence across four squads, but it does not show the size of the broader product organisation or ownership of an executive-level operating model.', type: 'not-evidenced' },
    { label: 'Explicit ambiguity examples', detail: 'A practical approach is part of the brief, but the resume does not include a concrete example of making a decision with incomplete information.', type: 'not-evidenced' },
  ],
  watchOuts: [
    'The remit is broad: planning, insight, metrics, launch readiness and coaching could be a genuinely strategic role or a catch-all coordination role. Ask what decisions this person owns.',
    'Salary and reporting line are not included, so there is not enough information yet to judge practical fit or the seniority of the role.',
  ],
  improvements: [
    'Add the size and shape of the product organisation you support at Northstar (squads, PMs or product surface area) to make your seniority easier to assess.',
    'Bring the 35% feedback-system result into the top third of your resume and name the decisions it changed, not only the system you built.',
    'Prepare one interview story about a messy prioritisation decision where customer evidence, commercial pressure and delivery constraints conflicted.',
  ],
  nextStep: 'Apply, but use the first conversation to pin down the decision rights, reporting line and salary range. Your evidence is strong enough to earn that conversation.',
  uncertainty: 'The assessment is limited by the ad’s missing salary and reporting-line details, and by limited evidence of executive-level scope in the resume.',
};
